<template>
  <section>
    <v-dialog
      v-model="showModal"
      max-width="800px"
    >
      <v-card
        shaped
        color="#ECEFF1"
      >
        <br>
        <h2 class="title-style">
          <v-icon
            class="icon-style"
            left
          >
            mdi-alert-circle-outline
          </v-icon>Create Ticket
        </h2>
        <v-card-text>
          <v-form
            ref="form"
            v-model="isFormValid"
          >
            <v-container>
              <v-row class="text-center">
                <v-col
                  cols="12"
                  md="12"
                >
                  <v-text-field
                    v-model="ticketName"
                    label="Ticket Name*"
                    hint="Name for your reference - 'No-Display' (Only alpha dashes allowed)."
                    :rules="[(v) => !!v || 'This field is required']"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-autocomplete
                    v-model="organizationId"
                    :items="organizations"
                    :item-text="getOrganizationName"
                    item-value="external_id"
                    label="Organization Id*"
                    :rules="[(v) => !!v || 'This field is required']"
                    hint="Name for your reference - 'DBSYS'"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="clientReferenceNumber"
                    label="Client Reference Number"
                    hint="Reference number prefered by the client."
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="internalReferenceNumber"
                    label="Internal Reference Number"
                    hint="Reference number from jira."
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                >
                  <v-select
                    v-model="selectedType"
                    :items="type"
                    label="Select Type*"
                    :rules="rules.select"
                    menu-props="auto"
                    prepend-icon="mdi-select"
                    @change="isTypeCms"
                  />
                </v-col>
                <v-col
                  v-if="isAppModuleDigitalSignage"
                  cols="12"
                  md="12"
                >
                  <v-autocomplete
                    ref="typeCheck"
                    v-model="selectedDigiBoardId"
                    :items="digiBoards"
                    item-value="external_id"
                    item-text="external_id"
                    label="Select Device"
                    prepend-icon="mdi-television"
                    hint="Select the device location, only if your issue type is related to a device."
                    :disabled="disableDigiBoard"
                    persistent-hint
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="12"
                >
                  <v-textarea
                    v-model="issueDescription"
                    filled
                    label="Description about the issue*"
                    :rules="[(v) => !!v || 'This field is required']"
                  />
                </v-col>
                <v-col
                  cols="12"
                  md="12"
                >
                  <v-autocomplete
                    v-model="reportedBy"
                    :items="users"
                    item-value="external_id"
                    :item-text="getFullName"
                    label="Reported By*"
                    :rules="[(v) => !!v || 'This field is required']"
                    hint="External id of the reported user."
                  />
                </v-col>
              </v-row>
            </v-container>
            <br>
            <p class="text-sm-left">
              *indicates required field
            </p>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="red darken-3"
            @click="showModal = false"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            color="#FF3700"
            @click="submit()"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
      <centre-spinner
        :loading="loading"
      />
    </v-dialog>
  </section>
</template>

<script>
  import Constants from 'src/constants';
  import spinner from 'src/views/dashboard/component/SpinnerCentre';

  export default {
    name: 'SupportForm',
    components: {
      'centre-spinner': spinner,
    },
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      appModuleName: {
        type: String,
        required: true,
      },
    },
    data () {
      return {
        isFormValid: false,
        ticketName: '',
        issueDescription: '',
        selectedDigiBoardId: null,
        selectedType: '',
        reportedBy: '',
        type: ['CMS', 'Device', 'SaaS', 'App'],
        organizationId: '',
        internalReferenceNumber: null,
        clientReferenceNumber: null,
        rules: {
          select: [(v) => !!v || 'This field is required'],
        },
        loading: false,
        showModal: true,
      };
    },
    computed: {
      digiBoards () {
        return this.$store.getters['digiboards/getDigiBoardsList'];
      },
      organizations () {
        return this.$store.getters['organizations/getOrganizationList'];
      },
      users () {
        return this.$store.getters['users/getUsersList'];
      },
      disableDigiBoard () {
        if (this.selectedType !== 'Device') {
          return true;
        }
        return false;
      },
      isAppModuleDigitalSignage () {
        if (this.appModuleName === 'digital-signage') {
          return true;
        }
        return false;
      },
    },
    watch: {
      showModal: function (newval, oldval) {
        if (newval === false) {
          this.$emit('closed');
        }
      },
    },
    async mounted () {
      this.loading = true;
      await this.$store.dispatch('digiboards/fetchDigiBoardsList');
      this.checkAndLoadStates();
      this.loading = false;
    },
    methods: {
      checkAndLoadStates () {
        if (this.organizations.length === 0) {
          this.$store.dispatch('organizations/fetchOrganizationList');
        }

        if (this.users.length === 0) {
          this.$store.dispatch('users/fetchUsersList');
        }
      },
      async submit () {
        if (this.$refs.form.validate() && this.appModuleName === 'digital-signage') {
          this.$store.dispatch('support/createIssueTicket', {
            ticketDetails: {
              name: this.ticketName,
              type: this.typeMapper(),
              digi_board_id: this.selectedDigiBoardId,
              description: this.issueDescription,
              client_reference: this.clientReferenceNumber,
              internal_reference: this.internalReferenceNumber,
              organization_id: this.organizationId.toUpperCase(),
              reported_by: this.reportedBy.toUpperCase(),
            },
            appModuleName: this.appModuleName,
          }).then(response => {
            this.$store.dispatch('alert/onAlert', {
              message: 'Ticket created successfully.',
              type: Constants.ALERT_TYPE_SUCCESS,
            });
            this.$emit('get-latest-tickets', this.appModuleName);
          });
          this.close();
        } else {
          this.$store.dispatch('support/createIssueTicket', {
            ticketDetails: {
              name: this.ticketName,
              type: this.typeMapper(),
              description: this.issueDescription,
              client_reference: this.clientReferenceNumber,
              internal_reference: this.internalReferenceNumber,
              organization_id: this.organizationId.toUpperCase(),
              reported_by: this.reportedBy.toUpperCase(),
            },
            appModuleName: this.appModuleName,
          }).then(response => {
            this.$store.dispatch('alert/onAlert', {
              message: 'Ticket created successfully.',
              type: Constants.ALERT_TYPE_SUCCESS,
            });
            this.$emit('get-latest-tickets', this.appModuleName);
          });
          this.close();
        }
      },
      close () {
        this.showModal = false;
        this.ticketName = '';
        this.issueDescription = '';
        this.selectedDigiBoardId = null;
        this.clientReferenceNumber = null;
        this.selectedType = '';
      },
      isTypeCms () {
        if (this.selectedType !== 'Device') {
          this.$refs.typeCheck.reset();
        }
      },
      typeMapper () {
        if (this.selectedType === 'Device') {
          return 'digiBoard';
        } else if (this.selectedType === 'SaaS') {
          return 'saas';
        } else if (this.selectedType === 'CMS') {
          return 'cms';
        } else if (this.selectedType === 'App') {
          return 'app';
        }
      },
      getFullName (item) {
        return `${item.first_name} ${item.last_name} - ${item.external_id}`;
      },
      getOrganizationName (item) {
        return `${item.name} - ${item.external_id}`;
      },
    },
  };
</script>
<style scoped>
.title-style {
  color: #37474F;
  margin-left: 30px;
}
.icon-style {
  color: #37474F;
  font-size: 45px;
}
</style>
